import React from 'react';
import _ from 'underscore';
import { jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import t from 'react-translate';
import { RootState } from 'redux/schemas';
import { useAppDispatch } from 'redux/store';
import LoadingRow from 'shared/components/loading-row';
import { getCoursesList, getJourneyClonings } from 'redux/actions/institutions';
import JourneyRow from 'learning_journeys/components/journey-row';
import { screenXsMax } from 'styles/global_defaults/media-queries';
import { PagedDataQueryParams } from 'redux/create-action-creators';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import {
  Course,
  Sorting,
  CoursesNormalized,
} from 'redux/schemas/models/course';
import {
  tripleSpacing,
  createGridStyles,
} from 'styles/global_defaults/scaffolding';
import NvResponsiveTable, {
  ColumnSortings,
} from 'shared/components/nv-responsive-table';
import { JourneyCloning } from 'redux/schemas/models/institution';
import { config } from '../../../config/pendo.config.json';

const LoadingRowWithoutAvatar = (props: { rowIndex: number }) => (
  <LoadingRow
    hideAvatar
    rowIndex={props.rowIndex}
  />
);

const initialFetchParams = {
  sorting: [Sorting.RELEASE_DATE_DESC],
};

const createTableColumns = () => [
  {
    name: t.COURSES.DASHBOARD_TABLE.OFFERING_NAME(),
    className: 'name-cell',
    gridWidth: '30%',
  },
  {
    name: t.COURSES.DASHBOARD_TABLE.NUM_OF_COURSES(),
    className: 'num-courses',
    gridWidth: '10%',
  },
  {
    name: t.COURSES.DASHBOARD_TABLE.RELEASE_DATE(),
    className: 'release-date-cell',
    sortable: true,
    gridWidth: '10%',
    headerTooltip: t.COURSES.DASHBOARD_TABLE.RELEASE_DATE_TOOLTIP(),
    dataQa: config.pendo.learningJourneys.releaseDateDownActiveIcon,
  },
  {
    name: t.COURSES.DASHBOARD_TABLE.CLOSE_DATE(),
    className: 'close-date-cell',
    sortable: true,
    gridWidth: '10%',
  },
  {
    name: t.COURSES.DASHBOARD_TABLE.NUM_ENROLLEES(),
    className: 'num-enrollees-cell',
    gridWidth: '10%',
  },
  {
    name: t.COURSES.DASHBOARD_TABLE.COMPLETION_RATE(),
    className: 'completion-rate-cell',
    gridWidth: 'minmax(40px, 30%)',
  },
  {
    name: '',
    className: 'options-cell',
    gridWidth: `${tripleSpacing}px`,
  },
];

const createMobileTableColumns = () => {
  const tableColumns = createTableColumns();
  const columns = [_.clone(tableColumns[0]), tableColumns[tableColumns.length - 1]];

  columns[0].gridWidth = `calc(100% - ${tableColumns[tableColumns.length - 1].gridWidth})`;

  return columns;
};

type Props = {
  className?: string,
};

const Dashboard = (props: Props) => {
  const {
    className,
  } = props;

  const dispatch = useAppDispatch();
  const currentInstitution = useSelector(getCurrentInstitution);
  // Desc sort the Release Date column by default
  const [columnSortings, setColumnSortings] = React.useState<ColumnSortings>({ 2: true });
  const getStoreCourseData = (state: RootState) => state.models.courses as CoursesNormalized;
  const [pagedFetchParams, setPagedFetchParams] = React.useState<PagedDataQueryParams>(initialFetchParams);
  const [journeyClonings, setJourneyClonings] = React.useState<Record<string, JourneyCloning[]>>({});
  const isHandheld = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });
  const columns = isHandheld ? createMobileTableColumns() : createTableColumns();

  const rowProps = {
    institution: currentInstitution,
  };
  const fetchParams = {
    institutionId: currentInstitution.id,
  };

  const isRowDisabled = (course: Course) => course.isBeingDeleted;

  const onHeaderSortingClicked = (colIndex: number) => {
    // NOTE: Currently this is hardcoded to only support sorting on columns #2 and #3
    if (colIndex !== 2 && colIndex !== 3) {
      return;
    }

    const newSorting = !columnSortings[colIndex];
    let newSortKey: string = null;

    if (colIndex === 2) {
      newSortKey = newSorting ? Sorting.RELEASE_DATE_DESC : Sorting.RELEASE_DATE_ASC;
    } else if (colIndex === 3) {
      newSortKey = newSorting ? Sorting.CLOSE_DATE_DESC : Sorting.CLOSE_DATE_ASC;
    }

    // Don't preserve anything from the previous sorting; we only allow sorting
    // on one column at a time
    setColumnSortings({ [colIndex]: newSorting });
    setPagedFetchParams({
      sorting: [newSortKey],
    });
  };

  const getCourses = (params) => getCoursesList({
    ...params,
    journeysOnly: true,
  });

  const clearSearchResults = () => setPagedFetchParams(initialFetchParams);

  const journeyCloningsState = useSelector((state: RootState) => state.models.journeyClonings);

  React.useEffect(() => {
    dispatch(getJourneyClonings({ institutionId: currentInstitution.id }));
  }, [currentInstitution.id, dispatch]);

  // Map journeys to their in-progress course clonings
  React.useEffect(() => {
    if (journeyCloningsState) {
      const journeysToClones: Record<string, JourneyCloning[]> = {};
      // Display the clones with the most recent clone requests at the top
      _.sortBy(journeyCloningsState, 'id').reverse().forEach(cloning => {
        if (!journeysToClones[cloning.owner.catalogId]) {
          journeysToClones[cloning.owner.catalogId] = [];
        }
        journeysToClones[cloning.owner.catalogId].push(cloning);
      });
      setJourneyClonings(journeysToClones);
    }
  }, [journeyCloningsState]);

  return (
    <NvResponsiveTable<Course>
      columns={columns}
      dataKey='catalogId'
      rowProps={rowProps}
      className={className}
      backgroundColor='#fff'
      cacheDataKey='catalogId'
      fetchParams={fetchParams}
      fetchData={getCourses as any}
      style={createGridStyles(1, 3)}
      columnSortings={columnSortings}
      checkRowDisabled={isRowDisabled}
      cacheLookup={getStoreCourseData}
      clearSearch={clearSearchResults}
      pagedFetchParams={pagedFetchParams}
      onSortClicked={onHeaderSortingClicked}
      loadingComponent={LoadingRowWithoutAvatar}
      rowComponent={JourneyRow as React.FunctionComponent}
      extraRowData={journeyClonings}
      extraRowTooltip={() => t.COURSES.DASHBOARD_TABLE.CLONING_IN_PROGRESS_DESCRIPTION()}
    />
  );
};

export default Dashboard;
